/**
 * Ideology System Constants
 *
 * Defines the 7-axis ideology system used throughout the game for:
 * - Cohort political positions and salience weights
 * - Party and candidate ideological profiles
 * - Voting calculations based on ideological distance
 * - Policy effects on ideology shifts
 *
 * Each axis ranges from -100 to +100, with negative values representing
 * the "left" position and positive values the "right" position.
 */

/**
 * The 7 ideology axes that define political positions in the game.
 * Each axis has a key for programmatic access, labels for UI display,
 * and a description explaining what the axis represents.
 */
export const IDEOLOGY_AXES = [
  {
    index: 0,
    key: 'economic_regulation',
    label: 'Economic Regulation',
    leftLabel: 'Free Market',
    rightLabel: 'Heavy Regulation',
    description: 'Government involvement in economic affairs',
  },
  {
    index: 1,
    key: 'social_values',
    label: 'Social Values',
    leftLabel: 'Traditional',
    rightLabel: 'Progressive',
    description: 'Stance on social norms and cultural change',
  },
  {
    index: 2,
    key: 'civil_liberties',
    label: 'Civil Liberties',
    leftLabel: 'Restrictive',
    rightLabel: 'Permissive',
    description: 'Individual freedoms vs collective security',
  },
  {
    index: 3,
    key: 'national_identity',
    label: 'National Identity',
    leftLabel: 'Nationalist',
    rightLabel: 'Cosmopolitan',
    description: 'National sovereignty vs international integration',
  },
  {
    index: 4,
    key: 'environment',
    label: 'Environment',
    leftLabel: 'Development',
    rightLabel: 'Green',
    description: 'Economic growth vs environmental protection',
  },
  {
    index: 5,
    key: 'foreign_security',
    label: 'Foreign & Security',
    leftLabel: 'Isolationist',
    rightLabel: 'Interventionist',
    description: 'Military involvement and foreign policy approach',
  },
  {
    index: 6,
    key: 'government_centralization',
    label: 'Centralization',
    leftLabel: 'Decentralized',
    rightLabel: 'Centralized',
    description: 'Distribution of governmental power',
  },
] as const;

/** Type for ideology axis keys */
export type IdeologyAxisKey = (typeof IDEOLOGY_AXES)[number]['key'];

/**
 * A 7-element tuple representing a position on each ideology axis.
 * Values range from -100 (left) to +100 (right).
 */
export type IdeologyVector = [
  number,
  number,
  number,
  number,
  number,
  number,
  number,
];

/** Minimum value for any ideology position */
export const IDEOLOGY_MIN = -100;

/** Maximum value for any ideology position */
export const IDEOLOGY_MAX = 100;

/** Minimum salience weight (issue doesn't matter at all) */
export const SALIENCE_MIN = 0;

/** Maximum salience weight (issue matters maximally) */
export const SALIENCE_MAX = 1;

/**
 * Default salience weights where all issues matter equally.
 * Used when no specific salience is provided.
 */
export const DEFAULT_SALIENCE: IdeologyVector = [1, 1, 1, 1, 1, 1, 1];

/**
 * Number of ideology axes in the system.
 * Used for iteration and validation.
 */
export const IDEOLOGY_AXIS_COUNT = 7;

/**
 * Maximum possible Euclidean distance between two ideology vectors
 * when using default (equal) salience weights.
 * Calculated as: sqrt(7 * 200^2) ≈ 529.15
 */
export const MAX_IDEOLOGY_DISTANCE = Math.sqrt(
  IDEOLOGY_AXIS_COUNT * Math.pow(IDEOLOGY_MAX - IDEOLOGY_MIN, 2)
);

/**
 * Position label thresholds for converting numeric values to human-readable labels.
 * Used by getPositionLabel() to describe where a value falls on an axis.
 */
export const POSITION_THRESHOLDS = {
  STRONG_LEFT: -60,
  MODERATE_LEFT: -20,
  MODERATE_RIGHT: 20,
  STRONG_RIGHT: 60,
} as const;
